// SOURCE: https://gist.github.com/ilkou/7bf2dbd42a7faf70053b43034fc4b5a4
import * as React from 'react';
import type { Props } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import {
  defaultClassNames,
  defaultStyles,
  isNodeInsideDismissableModal,
  resolveSelectMenuPortalTarget,
  withSelectMenuLayerStyles,
} from './helper';
import {
  ClearIndicator,
  DropdownIndicator,
  Menu,
  MenuList,
  MultiValueRemove,
  Option,
} from './react-select-custom-components';

type SharedCreatableProps = Props & {
  /**
   * When true (default outside dialogs), portal the menu to document.body so
   * sticky table headers do not cover it. Forced off inside Radix dialogs
   * unless `menuPortalTarget` is set explicitly (ENR-4012).
   */
  menuInPortal?: boolean;
};

const Creatable = React.forwardRef<
  React.ElementRef<typeof CreatableSelect>,
  SharedCreatableProps
>((props, ref) => {
  const {
    value,
    onChange,
    options = [],
    styles = defaultStyles,
    classNames = defaultClassNames,
    components = {},
    menuPortalTarget,
    menuPosition,
    menuInPortal,
    ...rest
  } = props;

  const id = React.useId();
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [isInsideDialog, setIsInsideDialog] = React.useState(false);

  React.useLayoutEffect(() => {
    setIsInsideDialog(isNodeInsideDismissableModal(hostRef.current));
  }, []);

  const portalTarget = resolveSelectMenuPortalTarget({
    menuPortalTarget,
    menuInPortal,
    isInsideDialog,
  });

  return (
    <div ref={hostRef} className='contents'>
      <CreatableSelect
        instanceId={id}
        ref={ref}
        value={value}
        onChange={onChange}
        options={options}
        unstyled
        components={{
          DropdownIndicator,
          ClearIndicator,
          MultiValueRemove,
          Option,
          Menu,
          MenuList,
          ...components,
        }}
        styles={withSelectMenuLayerStyles(styles)}
        classNames={classNames}
        menuPortalTarget={portalTarget}
        menuPosition={menuPosition ?? (portalTarget ? 'fixed' : undefined)}
        {...rest}
      />
    </div>
  );
});
Creatable.displayName = 'Creatable';
export default Creatable;
